import { useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Empty, Section, Spinner } from "@/components/kit";
import { api, brl, errMsg } from "@/lib/api";
import { AlertList, IND, can, useBiz, useBizGet } from "./shared";

const L = ({ items }) => (items?.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-slate-300">{items.map((x, i) => <li key={i}>{String(x)}</li>)}</ul> : <p className="text-sm text-slate-500">—</p>);
const PRI = { alta: "bg-red-500/15 text-red-300", media: "bg-amber-500/15 text-amber-300", baixa: "bg-sky-500/15 text-sky-300" };

export function Diagnosis() {
  const { cid, month, role, goTab } = useBiz();
  const { data: list, reload } = useBizGet("/diagnoses");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const processing = list?.some((d) => d.status === "processing");
  useEffect(() => {
    if (!processing) { return; }
    const t = setInterval(reload, 4000);
    return () => { clearInterval(t); };
  }, [processing, reload]);
  useEffect(() => { if (list?.length && (!sel || !list.find((d) => d.id === sel))) { setSel(list[0].id); } }, [list, sel]);
  if (!list) return <Spinner />;
  const run = async () => {
    setBusy(true);
    try { await api.post(`/biz/companies/${cid}/diagnoses`, { month }); toast.success("Diagnóstico iniciado. Leva cerca de 1 a 2 minutos."); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const d = list.find((x) => x.id === sel);
  const r = d?.result;
  return (
    <div className="space-y-6" data-testid="biz-diagnosis">
      <div className="card-p flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between">
        <div><h3 className="text-lg font-semibold">Diagnóstico de {month}</h3>
          <p className="text-sm text-slate-400">A IA usa somente os nove indicadores calculados pelo sistema, o histórico e os alertas. Ela não inventa números.</p></div>
        {can(role, "gestor") && <Button size="lg" onClick={run} disabled={busy || processing} className="font-bold tracking-wide" data-testid="biz-run-ai-btn">
          {processing ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Sparkles className="mr-2 h-5 w-5" />}{processing ? "ANALISANDO..." : "ANALISAR MINHA EMPRESA COM IA"}</Button>}
      </div>
      {!list.length ? <Empty text="Nenhum diagnóstico ainda. Lance os dados do mês e clique no botão acima." /> : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          <Section title="Histórico" testid="biz-diag-list">
            {list.map((x) => (
              <button key={x.id} onClick={() => setSel(x.id)} className={`mb-2 block w-full rounded-lg border p-3 text-left text-sm ${x.id === sel ? "border-[#D4AF37]/60" : "border-[#1E3A6E]"}`} data-testid="biz-diag-item">
                <div className="font-medium">Mês {x.month}</div>
                <div className="text-xs text-slate-500">{x.created_at.slice(0, 16).replace("T", " ")} · {x.status === "done" ? "concluído" : x.status === "processing" ? "processando" : "erro"}</div>
              </button>
            ))}
          </Section>
          <div className="space-y-5 lg:col-span-3">
            {d?.status === "processing" && <div className="card-p flex items-center gap-2 p-6 text-slate-300"><Loader2 className="h-5 w-5 animate-spin text-[#D4AF37]" /> Analisando os números da empresa...</div>}
            {d?.status === "error" && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">{d.error}</div>}
            {r && <>
              <Section title="A. Resumo executivo" testid="biz-diag-summary"><p className="whitespace-pre-line text-sm text-slate-200">{r.resumo_executivo}</p></Section>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <Section title="B. Pontos fortes"><L items={[...(r.pontos_fortes || []), ...(r.oportunidades || [])]} /></Section>
                <Section title="C. Pontos de atenção"><L items={r.pontos_atencao} /></Section>
              </div>
              <Section title="D. Plano de ação" action={<Button size="sm" variant="outline" className="border-[#1E3A6E] bg-transparent" onClick={() => goTab("plano")} data-testid="biz-go-plan">Acompanhar ações</Button>}>
                <div className="space-y-3">{(r.plano_acao || []).map((a, i) => (
                  <div key={i} className="rounded-lg border border-[#1E3A6E] p-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2"><span className={`rounded px-2 text-xs ${PRI[a.prioridade] || PRI.media}`}>{a.prioridade}</span><b>{a.recomendacao}</b></div>
                    <div className="mt-1 text-xs text-slate-400">Por quê: {a.porque || a.problema} · Evidência: {a.evidencia}</div>
                    <div className="mt-1 text-xs text-slate-500">Responsável: {a.responsavel} · Prazo: {a.prazo} · Resultado esperado: {a.beneficio_esperado}</div>
                  </div>))}</div>
              </Section>
              <Section title="E. Projeções (cenários calculados pelo sistema, 6 meses)">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">{Object.entries(d.scenarios || {}).map(([k, s]) => (
                  <div key={k} className="rounded-lg border border-[#1E3A6E] p-3 text-xs">
                    <div className="text-sm font-semibold capitalize">{k}</div><div className="text-slate-500">{s.premissas}</div>
                    <div className="mt-2">Lucro 6m: <b>{brl(s.lucro_12m)}</b> · Caixa final: <b>{brl(s.caixa_final)}</b></div>
                    <p className="mt-2 text-slate-300">{r.projecoes?.[k]}</p>
                  </div>))}</div>
              </Section>
              <Section title="F. Evolução"><p className="text-sm text-slate-300">{r.evolucao}</p></Section>
              <Section title="Limitações e dados usados"><L items={[...(r.limitacoes || []), ...(d.missing || [])]} /><div className="mt-3"><AlertList alerts={d.alerts} /></div></Section>
            </>}
          </div>
        </div>
      )}
    </div>
  );
}

const fmtV = (k, v) => (v == null ? "—" : ["margem_contribuicao", "ponto_equilibrio"].includes(k) ? `${v}%` : brl(v));

export function ActionPlan() {
  const { cid, role } = useBiz();
  const { data: acts, reload } = useBizGet("/actions");
  if (!acts) return <Spinner />;
  const set = async (a, status) => { try { await api.put(`/biz/companies/${cid}/actions/${a.id}`, { status }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Section title="Plano de ação gerencial" testid="biz-actions">
      {!acts.length ? <Empty text="As ações aparecem aqui depois do primeiro diagnóstico com IA." /> : (
        <div className="space-y-3">{acts.map((a) => (
          <div key={a.id} className="rounded-xl border border-[#1E3A6E] p-4" data-testid="biz-action-row">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2"><span className={`rounded px-2 text-xs ${PRI[a.prioridade]}`}>{a.prioridade}</span><b className="text-sm">{a.recomendacao}</b></div>
                <div className="mt-1 text-xs text-slate-400"><b>Problema:</b> {a.problema} · <b>Evidência:</b> {a.evidencia}</div>
                <div className="mt-1 text-xs text-slate-500">Responsável: {a.responsavel || "—"} · Prazo: {a.prazo || "—"} · Custo estimado: {a.custo_estimado || "—"} · Benefício: {a.beneficio_esperado || "—"}</div>
                {a.indicador && <div className="mt-1 text-xs text-sky-300" data-testid="biz-action-tracking">{IND[a.indicador]?.name}: {fmtV(a.indicador, a.baseline_value)} no diagnóstico ({a.month}) → {fmtV(a.indicador, a.current_value)} agora</div>}
              </div>
              <select value={a.status} disabled={!can(role, "gestor")} onChange={(e) => set(a, e.target.value)} data-testid="biz-action-status"
                className="h-9 rounded-md border border-[#1E3A6E] bg-[#0B1A33] px-2 text-sm">
                <option value="pendente">Pendente</option><option value="em_andamento">Em andamento</option><option value="concluido">Concluído</option>
              </select>
            </div>
          </div>))}</div>
      )}
    </Section>
  );
}
